import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayeeEngagementResponse,
  PayerPayeeEngagementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationUpdatePayeeEngagement } from "../../../query/payeeEngagements/mutations/useMutationUpdatePayeeEngagement";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";

export interface RestoreEngagementModalProps {
  payeeId: string;
  payeeName: string;
  engagement: IPayeeEngagementResponse;
  onClose: () => void;
}

export const RestoreEngagementModal: React.FC<RestoreEngagementModalProps> = ({
  payeeId,
  payeeName,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateEngagement, updateEngagementMeta] =
    useMutationUpdatePayeeEngagement();

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to restore this assignment?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Assignment name"
            value={engagement.engagementName}
          />
          <FieldView label="Contractor name" value={payeeName} />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring an assignment - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>New invoices and payments can be created for this assignment.</li>
          <li>
            Eligibility state for the assignment will be restored (including any
            changes that have occurred for eligibility requirements).
          </li>
        </WSElement>
      </WSAlert>

      <WSErrorMessage
        error={updateEngagementMeta.error}
        mb="XL"
        contextKey="ArchiveClient"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore assignment",
            loading: updateEngagementMeta.isLoading,
            kind: "Primary",
            onAsyncClick: async () => {
              await updateEngagement(
                {
                  payeeId,
                  engagementId: engagement.payerPayeeEngagementId,
                  status: PayerPayeeEngagementStatus.Active
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      theme: "dark",
                      type: "success",
                      message: "Assignment restored"
                    });
                    onClose();
                  }
                }
              );
            }
          }
        ]}
      />
    </>
  );
};

export const useRestoreEngagementModal = () => {
  return useWSModal(RestoreEngagementModal, {
    title: "Restore assignment",
    size: "M"
  });
};
